import { useState, type ReactNode } from 'react'
import { getDisplayLabel } from '~/entities/categories'
import {
  MATCH_SIDE_LABELS,
  type AgentRunDetail,
  type AgentSubmission,
  type MatchRowMark,
} from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import { AGREEMENT_STYLES, FIELD_HELP, SOURCES, type SourceKey } from '../config'
import {
  CATEGORY_PART_KEYS,
  describeFinalCaseSource,
  describeProductionOutcome,
  formatMethod,
  type Agreement,
  type CategoryInfo,
  type CodeRow,
  type ComparedCategory,
  type RunReview,
} from '../lib'
import { CodesBreakdown } from './codes-breakdown'
import {
  AgreementBadge,
  AgreementDot,
  AgreementLegend,
  EvidenceQuote,
  Hint,
  MaterialIcon,
  Mono,
  Muted,
  StorySection,
} from './primitives'

const GRID = 'grid grid-cols-[120px_repeat(3,minmax(0,1fr))]'
const PART_LABELS = { phase: 'Phase', outcome: 'Outcome', reason: 'Reason' } as const

function Cell({
  agreement,
  isReference = false,
  children,
}: {
  agreement?: Agreement
  isReference?: boolean
  children: ReactNode
}) {
  const tinted = agreement === 'agree' || agreement === 'partial' || agreement === 'differ'
  return (
    <div
      className={cn('relative flex min-w-0 flex-col gap-1.5 rounded-lg px-3 py-2.5', {
        'bg-surface-low': isReference,
        [`${AGREEMENT_STYLES[agreement ?? 'none'].fill}`]: tinted,
      })}
    >
      {children}
    </div>
  )
}

function RowLabel({ label, help, aside }: { label: string; help: string; aside?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-2.5 pr-2">
      <span className="text-text-primary flex items-center gap-1 text-[13px] leading-5 font-medium">
        {label}
        <Hint>{help}</Hint>
      </span>
      {aside}
    </div>
  )
}

function CellHeader({ agreement }: { agreement?: Agreement }) {
  // Before review the column header says so; a badge in every cell would only repeat it.
  if (!agreement || agreement === 'none' || agreement === 'pending') return null
  return (
    <div className="flex">
      <AgreementBadge agreement={agreement} />
    </div>
  )
}

/** A part's agreement as a glyph, readable without color. */
function PartMark({ agreement }: { agreement: Agreement }) {
  if (agreement !== 'agree' && agreement !== 'differ') return <AgreementDot agreement={agreement} />
  return (
    <MaterialIcon
      name={agreement === 'agree' ? 'check' : 'close'}
      className={cn('-mx-0.5 text-[14px] font-bold', AGREEMENT_STYLES[agreement].text)}
    />
  )
}

function CategoryValue({
  info,
  partAgreements,
}: {
  info: CategoryInfo
  partAgreements?: ComparedCategory['partAgreements']
}) {
  if (!info.slug) return <Muted>None</Muted>
  return (
    <>
      <span className="text-text-primary flex items-start gap-1 text-[13px] leading-5 font-medium wrap-anywhere">
        {info.label}
        {info.description && <Hint className="mt-0.5">{info.description}</Hint>}
      </span>
      {info.parts && (
        <dl className="m-0 flex flex-col gap-0.5">
          {CATEGORY_PART_KEYS.filter((key) => info.parts?.[key]).map((key) => (
            <div
              key={key}
              className="flex items-center gap-1.5 text-[12px] leading-4"
            >
              {partAgreements && <PartMark agreement={partAgreements[key]} />}
              <dt className="text-text-tertiary w-[52px] shrink-0">{PART_LABELS[key]}</dt>
              <dd className="text-text-secondary m-0 min-w-0 wrap-anywhere">
                {getDisplayLabel(info.parts![key]!)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </>
  )
}

const CODE_CHIP_STYLES = {
  agreed: 'bg-alert-success-fill text-success-content',
  extra: 'bg-alert-error-fill text-alert-error-content',
  missed: 'border border-dashed border-alert-error-message text-alert-error-content',
  neutral: 'bg-surface-medium text-text-primary',
} as const

/** A sign as well as a color, so added and missed codes read without relying on color. */
const CODE_CHIP_PREFIX = { agreed: '', extra: '+', missed: '−', neutral: '' } as const

function CodeChip({
  code,
  look,
  tip,
}: {
  code: string
  look: keyof typeof CODE_CHIP_STYLES
  tip?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-md px-1.5 font-mono text-[12px] leading-4',
        CODE_CHIP_STYLES[look],
      )}
      title={tip}
    >
      {CODE_CHIP_PREFIX[look]}
      {code}
    </span>
  )
}

function CodesValue({
  rows,
  who,
  hasFinal,
  hasResult = true,
  finalMissing = 'Not reviewed yet',
}: {
  rows: CodeRow[]
  who: SourceKey
  hasFinal: boolean
  hasResult?: boolean
  /** What the Final cell says when there is no tag review to compare to. */
  finalMissing?: string
}) {
  if (!hasResult) return <Muted>No result: the run did not finish</Muted>
  const chips = rows.flatMap((row) => {
    if (who === 'final')
      return row.inFinal
        ? [
            <CodeChip
              key={row.code}
              code={row.code}
              look="neutral"
            />,
          ]
        : []
    const present = who === 'agent' ? row.inAgent : row.inToday
    if (!hasFinal)
      return present
        ? [
            <CodeChip
              key={row.code}
              code={row.code}
              look="neutral"
            />,
          ]
        : []
    if (present) {
      return [
        <CodeChip
          key={row.code}
          code={row.code}
          look={row.inFinal ? 'agreed' : 'extra'}
          tip={row.inFinal ? 'Also in the final codes' : 'Not in the final codes'}
        />,
      ]
    }
    return row.inFinal
      ? [
          <CodeChip
            key={row.code}
            code={row.code}
            look="missed"
            tip="In the final codes, but missing here"
          />,
        ]
      : []
  })
  if (who === 'final' && !hasFinal) return <Muted>{finalMissing}</Muted>
  return chips.length ? (
    <div className="flex flex-wrap gap-1">{chips}</div>
  ) : (
    <Muted>No codes</Muted>
  )
}

function CaseIds({ ids }: { ids: string[] }) {
  return ids.length ? (
    <Mono className="text-[13px] leading-5 wrap-anywhere">{ids.join(' / ')}</Mono>
  ) : null
}

function CaseRow({ run, review }: { run: AgentRunDetail; review: RunReview }) {
  const match = review.match
  const side = match?.side
  const data = match?.sideData
  if (!match || !side || !data) return null
  const pick = run.match?.[side]?.pick
  const pickIds = data.row_marks.flat().find((mark: MatchRowMark) => mark.agent)?.ids ?? []
  const reviewer = data.reviewer
  const today = data.today
  const outcome = reviewer?.outcome

  return (
    <>
      <RowLabel
        label={`${MATCH_SIDE_LABELS[side]} case`}
        help={FIELD_HELP.match}
        aside={
          <a
            href="#patient-case"
            className="text-text-link text-[12px] leading-4 no-underline hover:underline"
          >
            Matching details
          </a>
        }
      />
      <Cell isReference>
        {!reviewer || reviewer.kind === 'pending' ? (
          <Muted>Not reviewed yet</Muted>
        ) : reviewer.kind === 'none' ? (
          <span className="text-[13px] leading-5 font-medium">No matching case</span>
        ) : reviewer.kind === 'unresolved' ? (
          <Muted>Could not be resolved</Muted>
        ) : (
          <CaseIds ids={reviewer.ids} />
        )}
        {reviewer?.source && <Muted>{describeFinalCaseSource(reviewer.source)}</Muted>}
      </Cell>
      <Cell agreement={match.agent}>
        <CellHeader agreement={match.agent} />
        {pick ? (
          <>
            <span className="text-[13px] leading-5">
              {pick.decision === 'none' ? 'No matching case' : <CaseIds ids={pickIds} />}
            </span>
            <Muted>
              {[pick.decision, pick.candidate, pick.confidence && `${pick.confidence} confidence`]
                .filter(Boolean)
                .join(' · ')}
            </Muted>
          </>
        ) : (
          <Muted>No decision</Muted>
        )}
      </Cell>
      <Cell agreement={match.today}>
        <CellHeader agreement={match.today} />
        {!today ? (
          <Muted>No lookup</Muted>
        ) : today.fired ? (
          <>
            <CaseIds ids={today.ids} />
            <Muted>{`${formatMethod(today.method)} rule · ${today.rows} candidates`}</Muted>
          </>
        ) : (
          <>
            <span className="text-[13px] leading-5">Abstained</span>
            <Muted>{`No rule picked among ${today.rows} candidates`}</Muted>
          </>
        )}
        {outcome && <Muted>{describeProductionOutcome(outcome)}</Muted>}
      </Cell>
    </>
  )
}

function AgentReasoning({ submission }: { submission: AgentSubmission }) {
  const quotes = submission.classification_quotes ?? []
  const confidence = submission.confidence
  return (
    <div className="flex flex-col gap-3">
      {confidence && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {CATEGORY_PART_KEYS.filter((key) => confidence[key]).map((key) => (
            <Muted key={key}>
              {`${PART_LABELS[key]}: `}
              <span className="text-text-primary font-medium">{`${confidence[key]} confidence`}</span>
            </Muted>
          ))}
        </div>
      )}
      {submission.explanation && (
        <p className="text-text-primary m-0 text-[13px] leading-5 whitespace-pre-wrap">
          {submission.explanation}
        </p>
      )}
      {quotes.length > 0 && (
        <div className="flex flex-col gap-1">
          <Muted>Quotes the agent based it on — click one to find it in the document</Muted>
          {quotes.map((quote, index) => (
            <EvidenceQuote
              key={index}
              text={quote}
            />
          ))}
        </div>
      )}
      {submission.consistency_note && (
        <Muted className="italic">{`Consistency note: ${submission.consistency_note}`}</Muted>
      )}
    </div>
  )
}

export function FinalStateSection({ run, review }: { run: AgentRunDetail; review: RunReview }) {
  const [showReasoning, setShowReasoning] = useState(
    !!run.final && review.category.agent.agreement !== 'agree',
  )
  const submission = (run.result?.submission ?? null) as AgentSubmission | null
  const { category, codes } = review
  const hasReasoning = !!(submission?.explanation || submission?.classification_quotes?.length)

  return (
    <StorySection
      id="final-state"
      title="Final state and how it compares"
      subtitle="What the document ended up as after review, next to what the agent and production produced. Each value is colored by how it compares to the final one."
      aside={<AgreementLegend />}
    >
      <div className="flex flex-col p-2">
        <div className={cn(GRID, 'gap-x-1 px-1 pt-1 pb-2')}>
          <span />
          {(['final', 'agent', 'today'] as const).map((key) => (
            <div
              key={key}
              className="flex flex-col px-3"
            >
              <span className="text-text-primary flex items-center gap-1 text-[13px] leading-5 font-semibold">
                {SOURCES[key].label}
                <Hint>{SOURCES[key].help}</Hint>
              </span>
              <Muted>
                {key === 'final' && !review.reviewed ? 'not reviewed yet' : SOURCES[key].caption}
              </Muted>
            </div>
          ))}
        </div>

        <div className={cn(GRID, 'border-divider gap-x-1 gap-y-1 border-t px-1 py-1.5')}>
          <RowLabel
            label="Category"
            help={FIELD_HELP.category}
            aside={
              hasReasoning && (
                <button
                  type="button"
                  onClick={() => setShowReasoning((value) => !value)}
                  className="text-text-link flex cursor-pointer items-center gap-0.5 border-none bg-transparent p-0 text-left text-[12px] leading-4 hover:underline"
                >
                  {showReasoning ? "Hide the agent's reasoning" : "The agent's reasoning"}
                </button>
              )
            }
          />
          <Cell isReference>
            <CategoryValue info={category.final} />
          </Cell>
          <Cell agreement={category.agent.agreement}>
            <CellHeader agreement={category.agent.agreement} />
            <CategoryValue
              info={category.agent}
              partAgreements={review.reviewed ? category.agent.partAgreements : undefined}
            />
          </Cell>
          <Cell agreement={category.today.agreement}>
            <CellHeader agreement={category.today.agreement} />
            <CategoryValue
              info={category.today}
              partAgreements={review.reviewed ? category.today.partAgreements : undefined}
            />
          </Cell>
        </div>

        {showReasoning && submission && hasReasoning && (
          <div className="bg-surface-low mx-1 mb-2 flex gap-3 rounded-lg p-4">
            <MaterialIcon
              name="auto_awesome"
              className="text-primary-main mt-0.5 text-[18px]"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-text-primary text-[13px] leading-5 font-semibold">
                {run.final
                  ? `Why the agent chose ${category.agent.label ?? 'this category'}`
                  : "The agent's last submission, which was not accepted"}
              </span>
              <AgentReasoning submission={submission} />
            </div>
          </div>
        )}

        <div className={cn(GRID, 'border-divider gap-x-1 gap-y-1 border-t px-1 py-1.5')}>
          <RowLabel
            label="Codes"
            help={FIELD_HELP.codes}
            aside={
              <>
                <a
                  href="#codes"
                  className="text-text-link text-[12px] leading-4 no-underline hover:underline"
                >
                  Code by code
                </a>
                {codes.hasFinal && (
                  <Muted className="text-text-tertiary">
                    <Mono>+</Mono> added, not in final
                    <br />
                    <Mono>−</Mono> in final, missing
                  </Muted>
                )}
              </>
            }
          />
          <Cell isReference>
            <CodesValue
              rows={codes.rows}
              who="final"
              hasFinal={codes.hasFinal}
              finalMissing={
                review.reviewed
                  ? 'No one reviewed the codes, so they are not scored'
                  : 'Not reviewed yet'
              }
            />
          </Cell>
          <Cell agreement={codes.agent.agreement}>
            <CellHeader agreement={codes.agent.agreement} />
            <CodesValue
              rows={codes.rows}
              who="agent"
              hasFinal={codes.hasFinal}
              hasResult={!!run.final}
            />
          </Cell>
          <Cell agreement={codes.today.agreement}>
            <CellHeader agreement={codes.today.agreement} />
            <CodesValue
              rows={codes.rows}
              who="today"
              hasFinal={codes.hasFinal}
            />
          </Cell>
        </div>

        {review.match?.sideData && (
          <div className={cn(GRID, 'border-divider gap-x-1 gap-y-1 border-t px-1 py-1.5')}>
            <CaseRow
              run={run}
              review={review}
            />
          </div>
        )}
      </div>

      <CodesBreakdown
        run={run}
        review={review}
      />
    </StorySection>
  )
}
